import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import SITE_DICT from "@/i18n/site";
import { setDocTitle, setTranslation, tr } from "@/i18n/translate";

// Website language: ?lang= > saved choice > the browser's language (if the server has it) > en. Remembered in localStorage.
const LANG_KEY = "cw_site_lang";
const baseCode = (c) => String(c || "").toLowerCase().split(/[-_]/)[0];
const SiteLangCtx = createContext(null);
export const useSiteLang = () => useContext(SiteLangCtx) || { lang: "en", languages: [], setLang: () => {} };

function firstChoice() {
  const q = baseCode(new URLSearchParams(window.location.search).get("lang"));
  let saved = null;
  try { saved = localStorage.getItem(LANG_KEY); if (q) localStorage.setItem(LANG_KEY, q); } catch { /* private mode */ }
  return q || saved || null;
}

function SiteLangProvider({ children }) {
  const list = usePublic("/i18n/languages");
  const [chosen, setChosen] = useState(firstChoice);
  const codes = list.data?.items?.map((l) => l.code);
  const browser = (navigator.languages || [navigator.language]).map(baseCode);
  const guess = chosen || browser[0] || "en";
  const lang = codes ? (codes.includes(guess) ? guess : browser.find((c) => codes.includes(c)) || "en") : guess;
  useEffect(() => { setTranslation(lang, SITE_DICT); }, [lang]);
  const setLang = useCallback((code) => {
    try { localStorage.setItem(LANG_KEY, code); } catch { /* private mode */ }
    const url = new URL(window.location.href);
    url.searchParams.set("lang", code);
    window.history.replaceState(window.history.state, "", url);
    setChosen(code);
  }, []);
  const value = useMemo(() => ({ lang, languages: list.data?.items || [], setLang }), [lang, list.data, setLang]);
  return <SiteLangCtx.Provider value={value}>{children}</SiteLangCtx.Provider>;
}

// Public data loader: GET /api/public/... (no auth). Returns {data, error, loading, reload}.
// Inside the website it adds ?lang= so Content comes back in the chosen language (and reloads when it changes).
export function usePublic(rawPath) {
  const lang = useContext(SiteLangCtx)?.lang;
  const path = rawPath && lang ? `${rawPath}${rawPath.includes("?") ? "&" : "?"}lang=${lang}` : rawPath;
  const [state, setState] = useState({ data: null, error: null, loading: !!path });
  const load = useCallback(() => {
    if (!path) return;
    setState((s) => ({ ...s, error: null, loading: true }));
    api(path, { auth: false })
      .then((data) => setState({ data, error: null, loading: false }))
      .catch((e) => setState({ data: null, error: e.message, loading: false }));
  }, [path]);
  useEffect(load, [load]);
  return { ...state, reload: load };
}

// The site bundle (GET /api/public/site) is loaded once by SiteLayout and shared with every page.
const SiteCtx = createContext({ data: null, error: null, loading: true, reload: () => {} });
function SiteData({ children }) {
  return <SiteCtx.Provider value={usePublic("/public/site")}>{children}</SiteCtx.Provider>;
}
export function SiteProvider({ children }) {
  return <SiteLangProvider><SiteData>{children}</SiteData></SiteLangProvider>;
}
export const useSite = () => useContext(SiteCtx);

// Per-route <title> + meta description (+ og:title / og:description), set on every route change.
// The title is fixed per route (set immediately); the description comes from live Content when loaded.
export const DEFAULT_DESCRIPTION = "Crushwink is calm, intentional dating. You Crush, they Wink back, you send the first message. No pressure. All intention.";
export function useSeo(title, description) {
  const full = title.includes("Crushwink") ? title : `${title} · Crushwink`;
  const desc = description || DEFAULT_DESCRIPTION;
  const { lang } = useSiteLang();
  useEffect(() => {
    setDocTitle(full);
    const set = (sel, val) => document.querySelector(sel)?.setAttribute("content", val);
    set('meta[name="description"]', tr(desc));
    set('meta[property="og:title"]', document.title);
    set('meta[property="og:description"]', tr(desc));
  }, [full, desc, lang]);
}

// First sentence-ish of a text, for meta descriptions.
export const summary = (text, n = 155) => {
  const t = String(text || "").replace(/^#+\s.*$/gm, "").replace(/\s+/g, " ").trim();
  return t.length > n ? `${t.slice(0, n - 1).trimEnd()}…` : t;
};
